import { Fragment, useState } from "react"
import { useTranslation, Trans } from "react-i18next"
import { Link, Outlet, useNavigate, useParams, useSearchParams } from "../navigation"
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { PencilSquareIcon, DocumentDuplicateIcon, ArrowDownTrayIcon } from "@heroicons/react/24/outline"
import { Formik, Form, Field } from "formik"
import clsx from "clsx"
import _ from 'lodash'

import EmptyState from "../components/EmptyState"
import { DatePicker } from "../components/input/DatePicker"
import { PlainImage } from "../components/Image"
import { competitor_claim } from "../data"
import { useRoles } from "../user/Auth"
import Modal from "../components/Modal"
import { DeleteButton, isoDateString } from "../components/edit-table"
import Editor from '../components/Editor'
import { useMessages } from "../Messages"
import { IconEdit, IconPlus } from "../components/icons"
import { useExternalMemberClaims } from "./ExternalMemberClaimsProvider"
import { useExternalClaims } from "./ExternalClaimsProvider"
import { useExternal } from "./"
import { CompetitorClaim } from "./external"


export default function CompetitorClaims() {
    return <CompetitorClaimsManager />
}

function CompetitorClaimsManager() {
    const { t } = useTranslation()
    const { currentClaims, memberClaims, familyClaims, updateClaim } = useExternalMemberClaims()
    const { isEditUser } = useRoles()

    const [onlyShowIndependent, setOnlyShowIndependent] = useState(false)
    const [selectedClaimId, setSelectedClaimId] = useState<number | null>(null)

    const selectedClaim = currentClaims.find(c => c.competitorClaimId === selectedClaimId) ?? null

    if (memberClaims?.length > 0)
        return (
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(28rem,40rem)_45ch] max-lg:divide-y-2 lg:divide-x-2 divide-pcx-200">
                <div className="max-lg:pb-4 lg:pr-4">
                    <div className="md:min-w-lg" id="claims-manager">
                        <div className="flex flex-col lg:flex-row gap-2 lg:gap-4 pb-2 max-lg:items-end">
                            <h3 className="grow max-lg:self-start">{t('claims-manager')}</h3>
                            {currentClaims?.length > 0 && <label className="inline-flex gap-1 items-center text-slate-500">
                                <input
                                    type="checkbox" className="form-checkbox focus:ring-0"
                                    checked={onlyShowIndependent}
                                    onChange={e => setOnlyShowIndependent(e.target.checked)} />
                                <div className="text-sm">{t('show-only-independent')}</div>
                            </label>}
                            <CompetitorClaimsDropdown />
                        </div>
                        <div className="flex flex-col gap-2 py-2">
                            {/* HEADER */}
                            <div className="flex md:items-center flex-col md:flex-row py-1 gap-4 mb-2">
                                <h4 className="sm:block grow">{t('claims')}</h4>
                                {isEditUser && <div className="flex flex-row gap-2 max-md:self-end text-sm">
                                    <Link to="import" className="btn-secondary py-0.5">{t('load-claims-from-epo')}</Link>
                                    <Link to="add" className="btn-secondary py-0.5">{t('add-claim')}</Link>
                                </div>}
                            </div>
                            {/* CLAIMS */}
                            {currentClaims
                                .filter(({ claimType }) => !onlyShowIndependent || claimType === "independent-claim")
                                .map((claim, index) => (
                                    <Fragment key={claim.competitorClaimId ?? -index}>
                                        <CompetitorClaimRow
                                            claim={claim}
                                            isSelected={claim.competitorClaimId === selectedClaimId}
                                            onSelect={() => setSelectedClaimId(claim.competitorClaimId)}
                                        />
                                    </Fragment>
                                ))}
                        </div>
                        <Outlet />
                    </div>
                </div>
                <div className="max-lg:pt-4 lg:pl-4">
                    <ClaimRemarkPanel
                        claim={selectedClaim}
                        onSave={(remark) => {
                            if (selectedClaim) updateClaim({ ...selectedClaim, remark })
                        }}
                    />
                </div>
            </div>
        )
    return (
        <div className="pt-0 pb-8 max-w-[52rem]">
            <EmptyState title={t('claims-manager')} text={t("empty-claims-text")} items={noClaimsActions(t, familyClaims?.length > 0).filter(Boolean)} />
            <Outlet />
        </div>
    )
}

const noClaimsActions = (t, hasFamilyClaims) => [
    hasFamilyClaims && {
        title: t("copy-claims"),
        description: t("copy-claims-desc"),
        icon: DocumentDuplicateIcon,
        background: 'bg-pcx-500',
        to: "version/add?copy=true",
    },
    {
        title: t("manually-add-claim"),
        description: t("manually-add-claim-desc"),
        icon: PencilSquareIcon,
        background: 'bg-pcx-300',
        to: "add",
    },
    {
        title: t("load-claims-from-epo"),
        description: t("load-claims-from-epo-desc"),
        icon: ArrowDownTrayIcon,
        background: 'bg-pcx-400',
        to: "import",
    },
]


function CompetitorClaimsDropdown() {
    const { t } = useTranslation()
    const { isEditUser } = useRoles()
    const { claimVersion, availableVersions, setClaimVersion } = useExternalMemberClaims()

    const itemClassName = clsx(
        'hover:bg-pcx-600 hover:text-white text-pcx-500',
        'group flex whitespace-nowrap w-full items-center rounded-md px-2 py-2'
    )

    return (
        <Menu as="div" className="relative inline-block text-left text-sm">
            <div>
                <MenuButton className={"btn-secondary inline-flex items-center gap-1 py-0.5 pl-2 pr-1"}>
                    <div> {`${t('version')}: ${claimVersion.version} (${claimVersion.versionDate})`}</div>
                    <ChevronDownIcon className="h-6 w-6" aria-hidden="true" />
                </MenuButton>
            </div>
            <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
            >
                <MenuItems className="absolute right-0 mt-1 w-fit origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <div className="px-1 py-1 ">
                        {isEditUser && <MenuItem>
                            <Link to="version/edit" className={itemClassName}>
                                <IconEdit className="mr-2 h-5 w-5" aria-hidden="true" /> {t('edit')}
                            </Link>
                        </MenuItem>}
                        {isEditUser && <MenuItem>
                            <Link to="version/add" className={itemClassName}>
                                <IconPlus className="mr-2 h-5 w-5" aria-hidden="true" /> {t('add-version')}
                            </Link>
                        </MenuItem>}
                        {availableVersions.map(({ version, versionDate }) =>
                            <MenuItem key={version}>
                                <button
                                    onClick={() => setClaimVersion({ version, versionDate })}
                                    className={itemClassName}
                                >
                                    <span className="pl-7">{`${t('version')} ${version} (${versionDate})`}</span>
                                </button>
                            </MenuItem>
                        )}
                    </div>
                </MenuItems>
            </Transition>
        </Menu>
    )
}

function CompetitorClaimRow({ claim, isSelected, onSelect }: { claim: CompetitorClaim; isSelected: boolean; onSelect: () => void }) {
    const { isEditUser } = useRoles()
    const { deleteClaim } = useExternalMemberClaims()

    return (
        <div
            className={clsx(
                "py-2 border-b border-pcx-100 last:border-b-0 cursor-pointer",
                isSelected && "bg-pcx-100 rounded"
            )}
            onClick={onSelect}
        >
            <div className="flex gap-2">
                <div className={clsx(
                    claim.claimType === 'independent-claim' ? "text-pcx-500 font-bold" : "text-slate-400 font-medium",
                    "text-right pr-1 min-w-[32px] shrink-0"
                )}>{claim.claimNumber}.</div>

                <div className="flex-grow min-w-0">
                    <div dangerouslySetInnerHTML={{ __html: claim.claimText ?? "" }} />
                </div>

                {isEditUser &&
                    <div className="flex flex-row gap-1 shrink-0">
                        <Link to={'' + claim.competitorClaimId} className="btn-primary w-6 h-6 p-1"><IconEdit /></Link>
                        <DeleteButton del={() => deleteClaim(claim)} small />
                    </div>}
            </div>
        </div>
    )
}

function ClaimRemarkPanel({ claim, onSave }: { claim: CompetitorClaim | null; onSave: (remark: string) => void }) {
    const { t } = useTranslation()
    const { isEditUser } = useRoles()

    const [isEditing, setIsEditing] = useState(false)
    const [remarkText, setRemarkText] = useState('')

    // Reset editing state when selected claim changes
    const claimId = claim?.competitorClaimId
    const [prevClaimId, setPrevClaimId] = useState(claimId)
    if (claimId !== prevClaimId) {
        setPrevClaimId(claimId)
        setIsEditing(false)
        setRemarkText(claim?.remark ?? '')
    }

    function startEditing() {
        setRemarkText(claim?.remark ?? '')
        setIsEditing(true)
    }

    function saveRemark() {
        onSave(remarkText)
        setIsEditing(false)
    }

    return (
        <div>
            <div className="pb-4 flex flex-row">
                <h4 className="grow">{t('remarks')}</h4>
            </div>
            {claim ? (
                isEditing ? (
                    <div className="flex flex-col gap-2">
                        <textarea
                            className="form-input text-sm w-full min-h-[6rem]"
                            value={remarkText}
                            onChange={e => setRemarkText(e.target.value)}
                            placeholder={t('remark')}
                            autoFocus
                        />
                        <div className="flex flex-row gap-2">
                            <button className="btn-primary text-sm py-0.5 px-2" onClick={saveRemark}>{t('save')}</button>
                            <button className="btn-secondary text-sm py-0.5 px-2" onClick={() => setIsEditing(false)}>{t('cancel')}</button>
                        </div>
                    </div>
                ) : (
                    <div>
                        {isEditUser &&
                            <div className="flex flex-row justify-end gap-1 mb-2">
                                <button onClick={startEditing} className="btn-primary w-6 h-6 p-1" title={t('edit')}><IconEdit /></button>
                                <DeleteButton del={() => onSave('')} small />
                            </div>}
                        {/* Subfamily-style card: image thumbnail + text, height-capped so it never grows indefinitely */}
                        <div className="p-4 bg-pc-100 dark:bg-pc-200 rounded-lg text-sm leading-relaxed min-h-[250px] max-h-[60vh] overflow-y-auto">
                            <div className="w-64 h-48 pb-2 mx-auto">
                                <PlainImage {...{ entity: competitor_claim, entityId: claim.competitorClaimId, text: claim.remark }} />
                            </div>
                            <div className="text-slate-600 whitespace-pre-wrap">
                                {claim.remark || <span className="text-slate-400 italic">{t('no-remarks')}</span>}
                            </div>
                        </div>
                    </div>
                )
            ) : (
                <div className="text-sm text-slate-400 italic">{t('select-claim')}</div>
            )}
        </div>
    )
}

export function EditAddCompetitorClaim() {
    const { t } = useTranslation()
    const { setErrorMessage } = useMessages()

    const navigate = useNavigate()
    const { claimId: _claimId } = useParams()
    const claimId = parseInt(_claimId)

    const { members } = useExternal()
    const { claims, isLoading } = useExternalClaims()
    const hasLoaded = !isLoading
    const originalClaim = claims.find(c => c.competitorClaimId === claimId)
    const isAdding = originalClaim === undefined

    const { competitorMemberId, addClaim, updateClaim, claimVersion: { version, versionDate }, currentClaims } = useExternalMemberClaims()

    const reservedClaimNumbers = currentClaims
        .filter(c => c.claimNumber !== originalClaim?.claimNumber)
        .map(c => c.claimNumber)

    const internalReference = members.find(m => m.competitorMemberId === competitorMemberId)?.internalReference ?? ''

    const initialValues: CompetitorClaim = !isAdding
        ? originalClaim
        : {
            claimNumber: Math.max(...[0, ...currentClaims.map(c => c.claimNumber)]) + 1,
            claimType: 'independent-claim',
            claimText: '',
            version,
            versionDate,
            competitorMemberId,
        }

    const labelStyle = "text-slate-800 w-32 inline-block font-medium pb-2"

    return (hasLoaded
        ? <Modal escAction={() => navigate('../')}>
            <Formik
                initialValues={initialValues}
                onSubmit={(claim) => {
                    const sanitizedClaim = { ...claim, claimText: claim.claimText ?? '' }
                    return (isAdding
                        ? addClaim(sanitizedClaim)
                        : updateClaim(sanitizedClaim))
                        .then(() => navigate(-1))
                        .catch(err => setErrorMessage(err.message))
                }}
                validate={(values) => {
                    const errors = {}
                    if ((values?.claimText ?? "").trim() === "")
                        errors['claimText'] = t("claim-text-required")
                    const claimNumber = +values.claimNumber
                    if (reservedClaimNumbers.includes(claimNumber))
                        errors['claimNumber'] = t("is-already-used")
                    return errors
                }}
            >{({ errors, touched }) =>
                <Form>
                    <div className="flex flex-col gap-4 p-4">
                        <h4>{isAdding
                            ? <Trans i18nKey="add-claim-for-name" values={{ name: internalReference }} />
                            : <Trans i18nKey="edit-claim-for-name" values={{ name: internalReference }} />
                        }</h4>
                        <div className="flex sm:flex-row flex-col gap-4">
                            <label>
                                <span className={labelStyle}>{t('claim-number')}</span> <br />
                                <Field name="claimNumber" className="form-input w-40" />
                                {touched.claimNumber && errors.claimNumber && <>
                                    <br /> <span className="text-red-700">{errors.claimNumber as string} </span>
                                </>}
                            </label>

                            <label><span className={labelStyle}>{t('claim-type')}</span> <br />
                                <Field name="claimType" className="form-select" as="select" >
                                    {['independent-claim', 'dependent-claim'].map(ct =>
                                        <option key={ct} value={ct}>{t(ct)}</option>
                                    )}
                                </Field>
                            </label>
                        </div>
                        <div>
                            <label htmlFor="claimText">
                                <span className={labelStyle}>{t('claim-text')}</span>
                                {touched.claimText && errors.claimText && <span className="text-red-700">{errors.claimText as string} </span>}
                            </label>
                            <div className="sm:h-[16rem] md:h-[24rem] md:min-w-[40rem] max-w-[48rem]">
                                <Editor name="claimText" />
                            </div>
                        </div>
                    </div>
                    <div className="bg-pc-200 p-4 flex flex-row-reverse gap-2">
                        <input disabled={touched.claimText && (errors.claimText ?? '') !== ''} type="submit" value={t("save")} className="btn-primary" />
                        <Link className="btn-secondary" to="../">{t('cancel')}</Link>
                    </div>
                </Form>
            }</Formik>
        </Modal>
        : null
    )
}

export function EditCompetitorClaimVersion() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { deleteClaim: deleteClaimApi, postClaim, reload } = useExternalClaims()
    const { claimVersion, currentClaims, setClaimVersion } = useExternalMemberClaims()

    function removeClaimVersion() {
        return Promise.all(currentClaims.map(deleteClaimApi))
            .then(() => {
                reload()
                setClaimVersion(undefined)
            })
            .then(() => navigate(-1))
    }

    function update(newVersion: { version: string, versionDate: string }) {
        return Promise.all(currentClaims.map(c => postClaim({ ...c, version: newVersion.version, versionDate: newVersion.versionDate })))
            .then(() => {
                reload()
                setClaimVersion(newVersion)
            })
            .then(() => navigate(-1))
    }

    return (
        <Modal escAction={() => navigate(-1)}>
            <Formik
                initialValues={claimVersion}
                onSubmit={(newVersion) => update(newVersion)}
                validate={(values) => {
                    const errors: { version?: string; versionDate?: string } = {}
                    if ((values.version ?? "").trim() === "")
                        errors.version = "Version must be set"
                    else if ((values.versionDate ?? "").trim() === "")
                        errors.versionDate = "Version Date must be set"
                    return errors
                }}
            >{({ touched, errors, dirty }) =>
                <Form>
                    <div className="flex flex-col p-4 gap-2">
                        <label className="w-full">
                            <div className="label mb-1">{t('version')} <span className="text-red-700">{touched.version && errors.version}</span></div>
                            <Field className="form-input w-full" name="version" />
                        </label>
                        <label className="w-full">
                            <div className="label mb-1">{t('versionDate')} <span className="text-red-700">{touched.versionDate && errors.versionDate}</span></div>
                            <Field className="form-input py-1 w-full" name="versionDate" as={DatePicker} />
                        </label>
                    </div>
                    <div className="p-4 bg-pc-200 flex flex-row-reverse gap-4">
                        <input className="btn-primary" type="submit" value={t("save")} />
                        {/* @ts-ignore */}
                        <Link to=".." className="btn-secondary">{t('cancel')}</Link>
                        <div className="grow" />
                        <input type="button" className="btn-warn disabled:btn-disabled"
                            disabled={dirty}
                            value={t("delete-claim-version")}
                            onClick={removeClaimVersion}
                        />
                    </div>
                </Form>
            }</Formik>
        </Modal>
    )
}

export function AddCompetitorClaimVersion() {
    const { t } = useTranslation()
    const navigate = useNavigate()

    const { internalReference } = useParams()

    const [searchParams] = useSearchParams()
    const copy = searchParams.get("copy") === "true"

    const { setClaimVersion, competitorMemberId, competitorFamilyId, familyClaims } = useExternalMemberClaims()

    const { members, memberById } = useExternal()
    const { postClaim, reload } = useExternalClaims()

    const familyMembers = members.filter(m => m.competitorFamilyId === competitorFamilyId)

    const relevantClaims = familyClaims
    const membersWithClaims = _.uniq(relevantClaims.map(c => c.competitorMemberId))

    const existingVersionDates = new Set(_(relevantClaims).filter(c => c.competitorMemberId === competitorMemberId).map(({ versionDate }) => versionDate).uniq().value())
    const existingVersions = new Set(_(relevantClaims).filter(c => c.competitorMemberId === competitorMemberId).map(({ version }) => version).uniq().value())

    const versionsByRef = _(familyMembers).map(({ internalReference, competitorMemberId }) => {
        const memClaims = relevantClaims.filter(c => c.competitorMemberId === competitorMemberId)
        const versions = _(memClaims)
            .map(({ version, versionDate }) => ({ version, versionDate }))
            .uniqWith(_.isEqual)
            .sortBy(v => v.versionDate)
            .reverse()
            .value()
        return [internalReference, versions]
    }).fromPairs().value()

    const refs = _(membersWithClaims).map(id => memberById[id]).filter(Boolean).sortBy(m => m.internalReference).value()

    function addNewVersion({ version, versionDate, isCopy, copyFrom, copyVersion: copyVersionIndex }) {
        if (!isCopy) {
            setClaimVersion({ version, versionDate })
            navigate("..")
        } else {
            const copyVersion = versionsByRef[copyFrom][copyVersionIndex]
            const copyMember = members.find(m => m.internalReference === copyFrom)
            const copyId = copyMember?.competitorMemberId
            const newClaims = relevantClaims.filter(c => c.competitorMemberId === copyId && c.version === copyVersion.version && c.versionDate === copyVersion.versionDate)
            Promise.all(newClaims.map(c => postClaim({ ...c, version, versionDate, competitorMemberId, competitorClaimId: undefined })))
                .then(() => {
                    reload()
                    setClaimVersion({ version, versionDate })
                })
                .then(() => navigate(".."))
        }
    }

    const initialCopyFrom = existingVersions.size > 0 ? internalReference : (refs[0]?.internalReference ?? "")
    const initialValues = { version: "", versionDate: isoDateString(new Date()), isCopy: copy, copyFrom: initialCopyFrom, copyVersion: 0 }

    return (
        <Modal>
            <Formik
                initialValues={initialValues}
                validate={(values) => {
                    const errors: { version?: string; versionDate?: string } = {}
                    if (values.version === undefined || values.version.trim() === "")
                        errors.version = t("is-required")
                    else if (existingVersions.has(values.version))
                        errors.version = t("is-already-used")
                    else if (values.versionDate === undefined || values.versionDate === "")
                        errors.versionDate = t("is-required")
                    else if (existingVersionDates.has(values.versionDate))
                        errors.versionDate = t("is-already-used")
                    return errors
                }}
                onSubmit={addNewVersion}
            >{({ values, touched, errors }) => {
                const versions = versionsByRef[values.copyFrom] ?? []
                return <Form>
                    <div className="flex flex-col gap-2 p-4 sm:max-w-md">
                        <h4 className="whitespace-no-wrap"><Trans i18nKey="add-new-claim-version-to" values={{ name: internalReference }} /></h4>
                        <label>
                            <div className="label mb-1 w-fit">{t('new-version')}
                                {touched.version && errors.version && <span className="text-red-600 inline normal-case">&nbsp; {errors.version as string} <br /></span>}
                            </div>
                            <Field className="form-input" name="version" autoFocus />
                        </label>

                        <label className="w-fit">
                            <div className="label mb-1">
                                {t('version-date')}
                                {touched.versionDate && errors.versionDate && <span className="text-red-600 inline normal-case">&nbsp; {errors.versionDate as string} <br /></span>}
                            </div>
                            <Field className="form-input py-2" name="versionDate" as={DatePicker} />
                        </label>

                        <div className="flex flex-col gap-2 min-w-fit">
                            {!copy && <label className="w-fit pt-2 flex flex-row items-center gap-2">
                                <Field className="form-checkbox" name="isCopy" type="checkbox" /> <span className="label normal-case">{t('create-copy')}</span>
                            </label>}

                            {values.isCopy && <>
                                <label>
                                    <span className="label mb-1">{t('copy-from')}</span>
                                    <Field className="form-select" name="copyFrom" as="select">
                                        {refs.map(r =>
                                            <option key={r.internalReference} value={r.internalReference}>{r.internalReference} ({r.countryCode})</option>
                                        )}
                                    </Field>
                                </label>

                                {values.copyFrom !== "" && <label>
                                    <span className="label mb-1">{t('copy-version')}</span>
                                    <Field className="form-select max-w-sm truncate" name="copyVersion" as="select">
                                        {_.map(versions, (v, vi) =>
                                            <option key={v.versionDate + ' ' + v.version} value={vi}>{v.version} ({v.versionDate})</option>
                                        )}
                                    </Field>
                                </label>}
                            </>}
                        </div>
                    </div>
                    <div className="flex flex-row-reverse gap-4 p-4 bg-pc-200">
                        <input type="submit" value={t("add")} className="btn-primary" />
                        <Link to=".." className="btn-secondary">{t('cancel')}</Link>
                    </div>
                </Form>
            }}</Formik>
        </Modal>
    )
}
